/**
 * The words and shapes the feedback screens share.
 *
 * Two vocabularies for one field, deliberately. The person writing the entry
 * reads a sentence about themselves ("Something got in my way"), because that
 * is what makes the choice obvious without a manual; the Administrator reading
 * fifty of them scans a one-word badge, because a column repeating a sentence
 * fifty times is a column nobody reads. Naming it "feedback" rather than
 * "fault logging" throughout is J3: people hesitate to file a fault about
 * their own confusion, which is exactly the entry worth having.
 */

import type { components } from "@/api/schema"
import type { TrailStep } from "@/lib/trail"

export type FeedbackCategory = components["schemas"]["FeedbackIn"]["category"]
export type FeedbackRow = components["schemas"]["FeedbackListOut"]
export type FeedbackEntry = components["schemas"]["FeedbackOut"]

/** The dialog's two choices, in the words the person reads. */
export const CATEGORY_CHOICES: {
  value: FeedbackCategory
  title: string
  detail: string
}[] = [
  {
    value: "problem",
    title: "Something got in my way",
    detail: "It did not work, or it was harder than it should have been.",
  },
  {
    value: "comment",
    title: "A comment or suggestion",
    detail: "An idea, a preference, or something you would like to see.",
  },
]

/** The review table's word for the same thing. */
export const CATEGORY_LABELS: Record<string, string> = {
  problem: "Problem",
  comment: "Comment",
}

export const CATEGORY_TONES: Record<string, "warning" | "info"> = {
  // Warning rather than danger: a person reporting an obstacle is not an
  // incident, and a table of red rows stops being read within a week.
  problem: "warning",
  comment: "info",
}

/** The prompt in the empty box, which is the only place the category changes the form. */
export const CATEGORY_PROMPTS: Record<FeedbackCategory, string> = {
  problem: "What were you trying to do, and what happened?",
  comment: "What would you change, and why?",
}

export type FeedbackContextParts = {
  trail: TrailStep[]
  viewport: { w: number; h: number } | null
  userAgent: string
}

/**
 * Read an entry's stored context back out.
 *
 * The server stores it verbatim as JSON and never reshapes it (the manual says
 * so, and it is the right call while the analysis is unwritten), which means
 * this side has to read it defensively: an entry submitted by an older build
 * carries whatever that build sent, and a review screen that throws on one row
 * shows nothing for any of them.
 */
export function contextParts(context: unknown): FeedbackContextParts {
  const record = (context ?? {}) as Record<string, unknown>
  const rawTrail = Array.isArray(record.trail) ? record.trail : []
  const trail: TrailStep[] = []
  for (const step of rawTrail) {
    const entry = step as { path?: unknown; at?: unknown }
    if (typeof entry.path !== "string") continue
    trail.push({ path: entry.path, at: typeof entry.at === "string" ? entry.at : "" })
  }

  const rawViewport = record.viewport as { w?: unknown; h?: unknown } | undefined
  const viewport =
    rawViewport && typeof rawViewport.w === "number" && typeof rawViewport.h === "number"
      ? { w: rawViewport.w, h: rawViewport.h }
      : null

  return {
    trail,
    viewport,
    userAgent: typeof record.userAgent === "string" ? record.userAgent : "",
  }
}
